/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  CloudFileInfo,
  InputFileInfo,
  RunningWorkflow,
} from './workflow.service';
import { SqdStatusState } from './workflow-status.service';
import { XdaStringsService } from './xda-strings.service';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  baseUrl = 'api/file/';
  currentFileId = '';
  currentFileName = '';

  constructor(
    private http: HttpClient,
    private xdaStringsService: XdaStringsService,
  ) {}

  uploadFile(attachment: InputFileInfo, selectedPages = '') {
    const formData: FormData = new FormData();

    if (attachment.name) {
      formData.append('attachment', attachment, attachment.name);
    }
    formData.append('selectedPages', selectedPages);
    return this.http.post<string>(`${this.baseUrl}UploadFile`, formData);
  }

  uploadFileFromCloudRepo(repoFile: CloudFileInfo) {
    return this.http.post<string>(
      `${this.baseUrl}UploadFileFromCloudRepo`,
      repoFile,
    );
  }

  downloadFile(id: string, workflowId: string) {
    return this.http.get(this.getDownloadFileUrl(id, workflowId), {
      observe: 'response',
      responseType: 'blob',
    });
  }

  getDownloadFileUrl(id: string, workflowId: string, userId?: string) {
    let downloadUrl = `${this.baseUrl}DownloadFile/${id}/${workflowId}`;
    // If a userId is passed in append it to the url
    if (userId) {
      downloadUrl += `?uId=${userId}`;
    }
    return downloadUrl;
  }

  getEditFileUrl(userId: string, workflow: RunningWorkflow) {
    const baseUrl = this.xdaStringsService.strings['APPBASEURL'];
    // uncomment the following line for debugging in local dev env
    // baseUrl = 'https://workflowcentral-test.services.xerox.com/';
    const fileId = workflow.input.fileId;
    const lastCompletedServiceId = workflow.services
      ?.slice()
      ?.reverse()
      ?.find(
        (x) => x.status && x.status[0].state == SqdStatusState.Completed,
      )?.id;
    return `${baseUrl}api/file/GetEditFile/${userId}/${workflow.id}/${fileId}/${lastCompletedServiceId}`;
  }

  saveEditFile(workflow: RunningWorkflow, editedFileUrl: string) {
    const fileId = workflow.input.fileId;
    const lastCompletedServiceId = workflow.services
      ?.slice()
      ?.reverse()
      ?.find(
        (x) => x.status && x.status[0].state == SqdStatusState.Completed,
      )?.id;

    const formData: FormData = new FormData();
    formData.append('editedFileUrl', editedFileUrl);

    return this.http.post(
      `${this.baseUrl}SaveEditFile/${fileId}/${lastCompletedServiceId}`,
      formData,
    );
  }

  downloadInterimFile(id: string, serviceId: string) {
    return this.http.get(this.getInterimDownloadFileUrl(id, serviceId), {
      observe: 'response',
      responseType: 'blob',
    });
  }

  validateMobileDownload(id: string, serviceId?: string) {
    let validationUrl = `${this.baseUrl}VerifyDownload/${id}`;
    if (serviceId) {
      validationUrl += `/${serviceId}`;
    }
    return this.http.get(validationUrl);
  }

  getInterimDownloadFileUrl(id: string, serviceId: string) {
    const downloadUrl = `${this.baseUrl}Download/${id}/${serviceId}`;
    return downloadUrl;
  }

  getPrintFileUrl(id: string) {
    const downloadUrl = `${this.baseUrl}DownloadPDFFile/${id}`;
    return downloadUrl;
  }

  getScanFileId() {
    return this.http.get<string>(`${this.baseUrl}GetScanFileId`);
  }

  get hasUploadedFileId() {
    return this.currentFileId != '';
  }
}
